const BASE_URL = process.env.REACT_APP_BASE_URL;
const noCacheFetchOptions = {
    cache: 'no-store',
    headers: {
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache'
    }
};

const pad2 = (value) => String(value).padStart(2, '0');

const formatDateTimeWithTZ = (date) => {
    const day = pad2(date.getDate());
    const month = pad2(date.getMonth() + 1);
    const year = date.getFullYear();

    const hh = pad2(date.getHours());
    const mm = pad2(date.getMinutes());
    const ss = pad2(date.getSeconds());

    const offsetMin = -date.getTimezoneOffset();
    const sign = offsetMin >= 0 ? '+' : '-';
    const abs = Math.abs(offsetMin);
    const offH = pad2(Math.floor(abs / 60));
    const offM = pad2(abs % 60);

    return `${day}/${month}/${year} ${hh}:${mm}:${ss} ${sign}${offH}'${offM}'`;
};

const refreshHtmlTemplateDate = (htmlTemplate, dateValue) => {
    if (typeof htmlTemplate !== 'string' || !htmlTemplate.includes('data-appearance-date')) {
        return { htmlTemplate, hasDateNode: false };
    }

    try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(htmlTemplate, 'text/html');
        const dateNodes = doc.querySelectorAll('[data-appearance-date]');
        if (!dateNodes.length) {
            return { htmlTemplate, hasDateNode: false };
        }

        const nextDate = formatDateTimeWithTZ(dateValue);
        dateNodes.forEach((node) => {
            node.textContent = nextDate;
        });

        return { htmlTemplate: doc.body.innerHTML, hasDateNode: true };
    } catch {
        return { htmlTemplate, hasDateNode: false };
    }
};

const normalizeAppearanceDates = (appearance) => {
    if (!appearance || typeof appearance !== 'object') {
        return appearance;
    }

    const now = new Date();
    const refreshed = refreshHtmlTemplateDate(appearance.htmlTemplate, now);
    if (!refreshed.hasDateNode) {
        return appearance;
    }

    const nextConfigJson = {
        ...(appearance.configJson || {}),
        appearance: {
            ...((appearance.configJson && appearance.configJson.appearance) || {}),
            text: {
                ...((appearance.configJson && appearance.configJson.appearance && appearance.configJson.appearance.text) || {}),
                dateSelectedAtIso: now.toISOString()
            }
        }
    };

    return {
        ...appearance,
        htmlTemplate: refreshed.htmlTemplate,
        configJson: nextConfigJson
    };
};

const getUserIdFromSession = () => {
    try {
        const raw = sessionStorage.getItem('userinfo');
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return typeof parsed?.uuid === 'string' ? parsed.uuid : null;
    } catch {
        return null;
    }
};

export const getSignatureAppearances = async (userId) => {
    const url = new URL(`${BASE_URL}/signature-appearances`);
    const resolvedUserId = userId ?? getUserIdFromSession();
    if (resolvedUserId) {
        url.searchParams.set('userId', String(resolvedUserId));
    }
    url.searchParams.set('ts', String(Date.now()));
    const response = await fetch(url.toString(), { method: 'GET', ...noCacheFetchOptions });
    if (!response.ok) {
        throw new Error('Failed to get signature appearances');
    }
    const result = await response.json();
    if (!Array.isArray(result)) {
        return result;
    }
    return result.map(normalizeAppearanceDates);
};

export const getSignatureAppearanceById = async (id) => {
    const url = new URL(`${BASE_URL}/signature-appearances/${id}`);
    url.searchParams.set('ts', String(Date.now()));

    const response = await fetch(url.toString(), { method: 'GET', ...noCacheFetchOptions });
    if (!response.ok) {
        throw new Error('Failed to get signature appearance');
    }
    const result = await response.json();
    return normalizeAppearanceDates(result);
};


export const createSignatureAppearance = async (payload) => {
    const url = `${BASE_URL}/signature-appearances`;
    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
    });
    if (!response.ok) {
        throw new Error('Failed to create signature appearance');
    }
    return await response.json();
};

export const updateSignatureAppearance = async (id, payload) => {
    const url = `${BASE_URL}/signature-appearances/${id}`;
    const response = await fetch(url, {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
    });
    if (!response.ok) {
        throw new Error('Failed to update signature appearance');
    }
    return await response.json();
};

export const deleteSignatureAppearance = async (id) => {
    const url = `${BASE_URL}/signature-appearances/${id}`;
    const response = await fetch(url, {
        method: 'DELETE',
        headers: {
            'Content-Type': 'application/json'
        }
    });
    if (!response.ok) {
        throw new Error('Failed to delete signature appearance');
    }
};
